import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Form, Row, Spinner, Tabs, Tab } from "react-bootstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { GoAlert } from "react-icons/go";
import { VscReplace } from "react-icons/vsc";
import { BiEdit } from "react-icons/bi";
import DOMPurify from "dompurify";
import * as moment from "moment";
import FormAlert from "../../components/UI/FormAlert/FormAlert";
import useApiClient, { apiRoutes } from "../../hooks/apiClient";
import CheckBoxProcedimentosFormik from "../../components/UI/Checkbox/CheckBoxProcedimentosFormik";
import SelectInputCachedFormik from "../../components/UI/SelectInput/SelectInputCachedFormik";
import SelectDiretor from "../../components/UI/SelectInput/SelectDiretor";
import DateTimeInputFormik from "../../components/UI/DateTimeInput/DateTimeInputFormik";
import OverlayInfoAdicional from "../../components/UI/Overlays/OverlayInfoAdicional";
import HtmlEditor from "../../components/UI/HtmlEditorInput/HtmlEditor";
import ListaContribuinteAcaoFiscal from "../SPF/ListaContribuinteAcaoFiscal";
import ListaExecutores from "./ListaExecutores";
import TextInputFormik from "../../components/UI/TextInput/TextInputFormik";
import useGerarOrdemServico from "./hooks/useGerarOrdemServico";
import { signalrActions } from "../../redux/signalr";
import useSetGuard from "../../hooks/useSetGuard";
import useTextoPadraoModal from "./hooks/useTextoPadraoModal";
// import { RetornaDataToNumero } from "../../components/Formatters/DateTime";
import useBuscaTextoPadraoOs from "./hooks/useBuscaTextoPadraoOs";
import EfisModal from "../../components/UI/EfisModal/EfisModal";

const validationSchema = Yup.object().shape({
  sequencialTipoAcao: Yup.string().default("").required("Informe o tipo de ação fiscal"),
  dataInicio: Yup.string()
    .required("Informe a data inicial da fiscalização")
    .test("data-inicial-valida", "Data inicial inválida.", (value) => {
      const isDataValida = moment(new Date(value), "DD/MM/YYYY", true).isValid();
      const ano = new Date(value).getFullYear() + 1;
      if (!isDataValida) {
        return false;
      }
      if (ano < 1901) {
        return false;
      }
      return true;
    }),
  dataFim: Yup.string()
    .required("Informe a data final da fiscalização")
    .test("data-final-valida", "Data final inválida.", (value) => {
      const isDataValida = moment(new Date(value), "DD/MM/YYYY", true).isValid();
      const ano = new Date(value).getFullYear() + 1;
      if (!isDataValida) {
        return false;
      }
      if (ano < 1901) {
        return false;
      }
      return true;
    }),
  contribuintes: Yup.array()
    .default([])
    .test("qtd-contribuinte", "Selecione pelo menos 1 contribuinte", (value) => (value || []).length > 0),
  textoPadrao: Yup.string()
    .default("")
    .test("texto-padrao", "Informe o Texto Complementar", (value) => {
      if ((value || []).length === 0 || value === undefined || value === null || value === "<p><br></p>") return false;
      return true;
    }),
  quantidadeDiasOs: Yup.number()
    .required("Informe a quantidade máxima de dias da OS")
    .positive()
    .integer()
    .min(1, "Valor mínimo deve ser 1")
    .max(60, "Valor máximo deve ser 60"),
  procedimentos: Yup.array().default([]),
  codigoDiretor: Yup.string().default("").required("Informe o Diretor"),
  listaExecutoresOs: Yup.array()
    .default([])
    .test("qtd-executor", "Selecione pelo menos 1 executor", (value) => (value || []).length > 0),
  numeroAberturaProcesso: Yup.string().default(""),
  sequencialDenuncia: Yup.string().default(""),
  sequencialParteServico: Yup.string().default(""),
  textoPadraoOs: Yup.string().default(""),
});

const initialValues = {};

validationSchema._nodes.forEach((key) => {
  if (key === "procedimentos" || key === "contribuintes" || key === "listaExecutoresOs") {
    initialValues[key] = [];
  } else if (key === "quantidadeDiasOs") {
    initialValues[key] = 60;
  } else {
    initialValues[key] = "";
  }
});

const FormGerarOrdemServico = ({ history }) => {
  const [sqOrgao, setSqOrgao] = useState();
  const [sgOrgao, setSgOrgao] = useState("");
  const [listaOs, setListaOs] = useState([]);
  const setGuardClient = useSetGuard();
  const { filaAssinaturaSucessoStateKey, filaAssinaturaFalhaStateKey } =
    useSelector((stateSignalr) => stateSignalr.signalr) || {};

  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues,
    validationSchema,
  });

  const { textoPadraoModal, setTextoPadraoModalShow, setConteudo } = useTextoPadraoModal();
  const { textoPadrao } = useBuscaTextoPadraoOs({ formik });

  const [showTextoOs, setShowTextoOs] = useState(true);
  const [showViewSubstituicao, setShowViewSubstituicao] = useState(false);
  const [textoBody, setTextoBody] = useState("");
  const [desabilitarBotao, setDesabilitarBotao] = useState(false);

  const { setValues } = formik;

  const preVisualizar = () => {
    const dados = {
      sqTipoAcao: formik.values.sequencialTipoAcao,
      texto: formik.values.textoPadrao,
      executores: formik.values.listaExecutoresOs,
      dataIni: formik.values.dataInicio,
      dataFim: formik.values.dataFim,
      procedimentos: formik.values.procedimentos,
    };
    setTextoPadraoModalShow(true);
    setConteudo(dados);
  };

  const { gerarOrdemServico, gerarOrdemServicoIsLoading, gerarOrdemServicoErrors, gerarOrdemServicoData } =
    useGerarOrdemServico({ formik });

  const { request: requestSafePayload, data: dataSafePayload } = useApiClient();
  const { request: requestOrgao, data: dataOrgao, errors: errorOrgao, isLoading: isLoadingOrgao } = useApiClient();

  useEffect(() => {
    requestOrgao(apiRoutes.fiscalizacao.ordemservico.consultaOrgaoLogado);
  }, [requestOrgao]);

  useEffect(() => {
    if ((!dataOrgao && !errorOrgao) || isLoadingOrgao) return;
    if (dataOrgao && dataOrgao.result.length) {
      setSqOrgao(dataOrgao.result[0].sequencialOrgao);
      setSgOrgao(dataOrgao.result[0].siglaOrgao);
    }
  }, [dataOrgao, errorOrgao, isLoadingOrgao]);

  // Assinador -------------------
  useEffect(() => {
    if (gerarOrdemServicoData) {
      const listaTicket = [];
      const listaOS = [];

      gerarOrdemServicoData.result.value.forEach((x) => {
        listaTicket.push(x.ticket);
        listaOS.push(x.nuOrdemServico);
      });
      setListaOs(listaOS);
      requestSafePayload(apiRoutes.usuarios.arquivo.selecionar, listaTicket);
    }
  }, [gerarOrdemServicoData, requestSafePayload]);

  const assinaturaHandler = useCallback(
    (safePayload) => {
      setGuardClient.assinar(safePayload);
    },
    [setGuardClient],
  );

  useEffect(() => {
    if (dataSafePayload) {
      assinaturaHandler(dataSafePayload.result);
    }
  }, [dataSafePayload, assinaturaHandler]);

  useEffect(() => {
    if (filaAssinaturaSucessoStateKey) {
      const texto = `Ordem(s) de serviço ${listaOs} gerada(s) com sucesso.`;
      toast.success(texto);
      history.push("/app/os/gerarOs");
    }
    return () => {
      dispatch(signalrActions.clearState("filaAssinaturaSucessoStateKey"));
    };
  }, [dispatch, filaAssinaturaSucessoStateKey, history, listaOs]);

  useEffect(() => {
    if (filaAssinaturaFalhaStateKey) {
      dispatch(signalrActions.clearState("filaAssinaturaFalhaStateKey"));
    }
  }, [dispatch, filaAssinaturaFalhaStateKey]);

  // -----------------------------

  useEffect(() => {
    setValues((curr) => ({
      ...curr,
      textoPadraoOs: textoPadrao,
    }));
  }, [setValues, textoPadrao]);

  return (
    <>
      {setGuardClient.linkRef}
      {textoPadraoModal}
      <EfisModal
        show={showViewSubstituicao}
        title="Texto da OS"
        size="lg"
        closeHandler={() => setShowViewSubstituicao(false)}
        primaryLabelVisible={false}
        secondaryLabel="Fechar"
        body={<span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(textoBody) }} />}
      />
      <h1 className="h3 mb-4 text-gray-800">Gerar Ordem de Serviço</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">Individual/Lote</h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={formik.values} keyFieldName="sequencialOrdemServico" />
                </Col>
              </Row>
            </div>
            <div className="card-body">
              <FormAlert
                formik={formik}
                data={gerarOrdemServicoData}
                errors={gerarOrdemServicoErrors}
                isLoading={gerarOrdemServicoIsLoading}
              />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <Tabs defaultActiveKey="geral" transition={false} id="tabSpf">
                  <Tab
                    eventKey="geral"
                    title={
                      <span>
                        <GoAlert
                          color="red"
                          size={15}
                          hidden={
                            !(
                              formik.errors.sequencialTipoAcaoFiscal ||
                              formik.errors.dataInicio ||
                              formik.errors.dataFim ||
                              formik.errors.DescricaoOs
                            )
                          }
                        />{" "}
                        Dados Gerais
                      </span>
                    }
                  >
                    <br />
                    <Row>
                      <Form.Group as={Col} sm={12} md={6}>
                        <Form.Label>Tipo de Ação Fiscal</Form.Label>
                        <SelectInputCachedFormik
                          formik={formik}
                          field="sequencialTipoAcao"
                          tipoItem="tipo-acao-fiscal-os"
                        />
                      </Form.Group>
                      <Form.Group className="form-group-periodos" as={Col} sm={12} md={6}>
                        <Form.Label>Período de Fiscalização</Form.Label>
                        <div className="form-inline">
                          <DateTimeInputFormik formik={formik} field="dataInicio" />
                          <Form.Label className="mx-3"> a </Form.Label>
                          <DateTimeInputFormik formik={formik} field="dataFim" />
                        </div>
                      </Form.Group>
                    </Row>
                    <Row>
                      <Form.Group as={Col} sm={12} md={12}>
                        <CheckBoxProcedimentosFormik formik={formik} field="procedimentos" />
                      </Form.Group>
                    </Row>
                    {sgOrgao === "COFIS" ? (
                      <Row>
                        <Form.Group as={Col} sm={12} md={6}>
                          <Form.Label>Setor</Form.Label>
                          <SelectInputCachedFormik formik={formik} field="setor" tipoItem="orgaos-os" />
                        </Form.Group>
                      </Row>
                    ) : null}
                    <Row>
                      <Form.Group as={Col} sm={12} md={6}>
                        <SelectDiretor formik={formik} field="codigoDiretor" sqorgaologado={sqOrgao} />
                      </Form.Group>
                      <Form.Group as={Col} sm={12} md={3}>
                        <Form.Label>N° Máximo de Dias da OS</Form.Label>
                        <TextInputFormik
                          formik={formik}
                          field="quantidadeDiasOs"
                          defaultValue={60}
                          maxLength="3"
                          type="number"
                        />
                      </Form.Group>
                    </Row>
                    <Row>
                      <Form.Group as={Col} sm={12} md={12}>
                        <Form.Label>
                          Texto Complementar
                          <VscReplace
                            className="btn-lg  text-danger pa-0 p-0 btn btn-bg-transparent ml-2"
                            title="Visualizar texto com substituições"
                            size={21}
                            onClick={() => preVisualizar()}
                          />
                        </Form.Label>
                        <HtmlEditor formik={formik} field="textoPadrao" />
                      </Form.Group>
                    </Row>

                    <Row>
                      <Col>
                        <hr />
                        <Form.Label>
                          <strong>Texto da OS</strong>
                          <BiEdit
                            className="btn-lg  text-danger pa-0 p-0 btn btn-bg-transparent ml-2"
                            title={showTextoOs ? "Editar Texto" : "Fechar Edição"}
                            onClick={() => {
                              setShowTextoOs(!showTextoOs);
                            }}
                          />
                          <VscReplace
                            className="btn-lg  text-danger pa-0 p-0 btn btn-bg-transparent ml-2"
                            title="Visualizar texto com substituições"
                            onClick={() => {
                              setShowViewSubstituicao(true);
                              setTextoBody(formik.values.textoPadraoOs);
                            }}
                          />
                        </Form.Label>
                        {!showTextoOs && <HtmlEditor formik={formik} field="textoPadraoOs" rows="5" />}
                        <hr hidden={!showTextoOs} />
                        <br />
                      </Col>
                    </Row>
                  </Tab>

                  <Tab
                    eventKey="contribuinte"
                    title={
                      <span>
                        <GoAlert color="red" size={15} hidden={!formik.errors.contribuintes} /> Contribuintes
                      </span>
                    }
                  >
                    <ListaContribuinteAcaoFiscal
                      formik={formik}
                      field="contribuintes"
                      documento={null} // {documentoContribuinte}
                      origem="OS"
                    />
                  </Tab>

                  <Tab
                    eventKey="listaExecutoresOs"
                    title={
                      <span>
                        <GoAlert color="red" size={15} hidden={!formik.errors.listaExecutoresOs} /> Executores
                      </span>
                    }
                  >
                    <ListaExecutores formik={formik} field="listaExecutoresOs" />
                  </Tab>
                </Tabs>

                <Row>
                  <Col>
                    {formik.isSubmitting ? (
                      <span>
                        <Spinner animation="border" role="status" /> Gerando Ordem de Serviço Individual/Lote...
                      </span>
                    ) : (
                      <Button
                        variant="primary"
                        className="mr-3"
                        disabled={desabilitarBotao || !formik.isValid}
                        onClick={() => {
                          gerarOrdemServico();
                          setDesabilitarBotao(true);
                        }}
                      >
                        Gerar OS
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="light" className="mr-3" onClick={formik.handleReset}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="danger" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormGerarOrdemServico;
